import * as React from "react";
import {
  Box, Card, CardContent, Grid, Typography, Stack, Chip, Skeleton, alpha,
  Table, TableHead, TableBody, TableRow, TableCell, LinearProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  People as PeopleIcon,
  MedicalServices as ProIcon,
  Event as CalendarIcon,
  Euro as EuroIcon,
  Star as StarIcon,
  VideoCall as VideoIcon,
  BusinessCenter as OfficeIcon,
  EmojiEvents as ReferralIcon,
  Email as EmailIcon,
  HourglassEmpty as PendingIcon,
  Flag as FlagIcon,
  Article as ArticleIcon,
} from "@mui/icons-material";
import { httpClient } from "../../api/httpClient";
import { API_BASE_URL } from "../../api/config";

const fmtNum = (n) => new Intl.NumberFormat("fr-FR").format(n || 0);
const fmtMoney = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);
const sum = (obj) => Object.values(obj || {}).reduce((a, b) => a + Number(b || 0), 0);
const lastMonth = (obj) => {
  const e = Object.entries(obj || {}).sort();
  return e.length ? Number(e[e.length - 1][1]) : 0;
};

// ── Hook : fetch un endpoint et retourne {data, loading, error} ─────────────
const useEndpoint = (path) => {
  const [state, setState] = React.useState({ data: null, loading: true, error: null });
  React.useEffect(() => {
    let alive = true;
    setState({ data: null, loading: true, error: null });
    httpClient(`${API_BASE_URL}${path}`)
      .then(({ json }) => alive && setState({ data: json, loading: false, error: null }))
      .catch((e) => alive && setState({ data: null, loading: false, error: e }));
    return () => { alive = false; };
  }, [path]);
  return state;
};

// ── Kpi compact ──────────────────────────────────────────────────────────────
const StatTile = ({ label, value, sub, icon: Icon, accent = "primary", to }) => {
  const theme = useTheme();
  const color = theme.palette[accent]?.main || theme.palette.primary.main;
  const inner = (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Box sx={{
        width: 44, height: 44, borderRadius: 2,
        display: "flex", alignItems: "center", justifyContent: "center",
        bgcolor: alpha(color, 0.12), color,
      }}>
        <Icon />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" sx={{
          color: "text.secondary", fontWeight: 600,
          textTransform: "uppercase", letterSpacing: 0.5,
        }}>
          {label}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </Box>
    </Stack>
  );
  return (
    <Card sx={{
      height: "100%",
      ...(to && {
        cursor: "pointer", transition: "transform 0.15s, box-shadow 0.15s",
        "&:hover": { transform: "translateY(-2px)", boxShadow: `0 8px 16px ${alpha(color, 0.15)}` },
      }),
    }} {...(to && { component: "a", href: to, style: { textDecoration: "none", color: "inherit" } })}>
      <CardContent sx={{ p: 2 }}>{inner}</CardContent>
    </Card>
  );
};

// ── Section card ─────────────────────────────────────────────────────────────
const Section = ({ title, children, action }) => (
  <Card sx={{ height: "100%" }}>
    <CardContent>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
        {action}
      </Stack>
      {children}
    </CardContent>
  </Card>
);

// ── Top-N table par valeur ───────────────────────────────────────────────────
const TopTable = ({ data, label = "Item", topN = 10, valueLabel = "Valeur", money }) => {
  const entries = Object.entries(data || {}).sort(([, a], [, b]) => Number(b) - Number(a)).slice(0, topN);
  if (entries.length === 0)
    return <Typography variant="body2" color="text.secondary">Aucune donnée</Typography>;
  const max = Math.max(...entries.map(([, v]) => Number(v)), 1);
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{label}</TableCell>
          <TableCell align="right">{valueLabel}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {entries.map(([k, v]) => (
          <TableRow key={k} hover>
            <TableCell>{k}</TableCell>
            <TableCell align="right">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-end" }}>
                <Box sx={{ width: 100, height: 6, bgcolor: "#eef4ff", borderRadius: 3, overflow: "hidden" }}>
                  <Box sx={{ width: `${(Number(v) / max) * 100}%`, height: "100%", bgcolor: "primary.main" }} />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {money ? fmtMoney(v) : fmtNum(v)}
                </Typography>
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

// ── Page Statistiques ────────────────────────────────────────────────────────
export const StatsScreen = () => {
  const stats = useEndpoint("/stats");
  const ura = useEndpoint("/admin/ura-transactions?size=200");
  const affil = useEndpoint("/admin/affiliation-transactions?size=200");
  const emails = useEndpoint("/admin/email-outbox?size=500");
  const discounts = useEndpoint("/admin/discount-bonus?size=200");
  const proRanking = useEndpoint("/ranking?page=0&size=10");
  const patientRanking = useEndpoint("/patient-ranking?page=0&size=10");

  const s = stats.data || {};
  const totalBookings = Math.max(sum(s.bookingsByMonth), sum(s.bookingsByDay));
  const totalReviews = sum(s.reviewsPerPro);
  const visio = s.bookingsByType?.VISIO || 0;
  const office = s.bookingsByType?.OFFICE || 0;
  const totalTyped = visio + office;
  const lastAvgPrice = (() => {
    const vals = Object.values(s.averagePriceByMonth || {});
    return vals[vals.length - 1] ?? 0;
  })();

  const uraRows = Array.isArray(ura.data) ? ura.data : [];
  const uraEarned = uraRows.filter(t => t.amount > 0).reduce((a, t) => a + Number(t.amount), 0);
  const uraSpent = uraRows.filter(t => t.amount < 0).reduce((a, t) => a + Math.abs(Number(t.amount)), 0);

  const affilRows = Array.isArray(affil.data) ? affil.data : [];
  const affilTotal = affilRows.reduce((a, t) => a + Number(t.amount || 0), 0);

  const emailRows = Array.isArray(emails.data) ? emails.data : [];
  const emailByStatus = emailRows.reduce((acc, e) => {
    const s = e.status || "?"; acc[s] = (acc[s] || 0) + 1; return acc;
  }, {});
  const emailFailed = emailByStatus.FAILED || 0;
  const emailPending = emailByStatus.PENDING || 0;
  const emailSent = emailByStatus.SENT || 0;

  const discountRows = Array.isArray(discounts.data) ? discounts.data : [];
  const discountUsed = discountRows.filter(d => d.used).length;
  const discountUnused = discountRows.length - discountUsed;

  const loadingHero = stats.loading;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Statistiques</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Monitoring complet : volumétrie, activité, économie, communication, modération.
      </Typography>

      {/* ── 1. Volumétrie ───────────────────────────────────────────── */}
      <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 700 }}>
        Volumétrie
      </Typography>
      <Grid container spacing={2} sx={{ mt: 0.5, mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          {loadingHero ? <Skeleton variant="rounded" height={92} /> :
            <StatTile label="Patients" value={fmtNum(s.totalPatients)} icon={PeopleIcon} accent="primary" to="#/patients" />}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {loadingHero ? <Skeleton variant="rounded" height={92} /> :
            <StatTile label="Professionnels" value={fmtNum(s.totalProfessionals)} icon={ProIcon} accent="secondary" to="#/professionals" />}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {loadingHero ? <Skeleton variant="rounded" height={92} /> :
            <StatTile label="Réservations" value={fmtNum(totalBookings)} icon={CalendarIcon} accent="info"
              sub={`Ce mois : ${fmtNum(lastMonth(s.bookingsByMonth))}`} to="#/bookings" />}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {loadingHero ? <Skeleton variant="rounded" height={92} /> :
            <StatTile label="Avis" value={fmtNum(totalReviews)} icon={StarIcon} accent="warning" to="#/reviews" />}
        </Grid>
      </Grid>

      {/* ── 2. Activité ───────────────────────────────────────────── */}
      <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 700 }}>
        Activité
      </Typography>
      <Grid container spacing={2} sx={{ mt: 0.5, mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          {loadingHero ? <Skeleton variant="rounded" height={92} /> :
            <StatTile label="Visio" value={fmtNum(visio)} icon={VideoIcon} accent="primary"
              sub={totalTyped > 0 ? `${Math.round((visio / totalTyped) * 100)}% des RDV typés` : null} />}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {loadingHero ? <Skeleton variant="rounded" height={92} /> :
            <StatTile label="Cabinet" value={fmtNum(office)} icon={OfficeIcon} accent="secondary"
              sub={totalTyped > 0 ? `${Math.round((office / totalTyped) * 100)}% des RDV typés` : null} />}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {loadingHero ? <Skeleton variant="rounded" height={92} /> :
            <StatTile label="Inscriptions" value={fmtNum(sum(s.registrationsByMonth))} icon={PeopleIcon} accent="info"
              sub={`Ce mois : ${fmtNum(lastMonth(s.registrationsByMonth))}`} />}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {loadingHero ? <Skeleton variant="rounded" height={92} /> :
            <StatTile label="Âge moyen patients" value={`${(s.averagePatientAge || 0).toFixed(1)} ans`} icon={PeopleIcon} accent="info" />}
        </Grid>
      </Grid>

      {/* Funnel — taux d'annulation et complétion ──────────────── */}
      {(() => {
        const byStatus = s.bookingsByStatus || {};
        const totalAll = Object.values(byStatus).reduce((a, b) => a + Number(b || 0), 0);
        const cancelled = ["CANCELED_BY_USER", "CANCELED_BY_PRO", "CANCELLED", "NO_SHOW", "PAYMENT_EXPIRED"]
          .reduce((acc, k) => acc + (Number(byStatus[k] || 0)), 0);
        const done = (Number(byStatus.DONE || 0)) + (Number(byStatus.FINISHED || 0)) + (Number(byStatus.INVOICED || 0));
        const cancelRate = totalAll > 0 ? Math.round((cancelled / totalAll) * 100) : 0;
        const completionRate = totalAll > 0 ? Math.round((done / totalAll) * 100) : 0;
        return (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile label="RDV terminés" value={fmtNum(done)} icon={CalendarIcon} accent="success"
                sub={`${completionRate}% des RDV`} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile label="RDV annulés / no-show" value={fmtNum(cancelled)} icon={FlagIcon}
                accent={cancelRate > 30 ? "error" : "warning"}
                sub={`${cancelRate}% des RDV`} />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <StatTile label="Total RDV (tous statuts)" value={fmtNum(totalAll)} icon={CalendarIcon} accent="info"
                sub="DONE + CANCEL + SCHEDULED + …" />
            </Grid>
          </Grid>
        );
      })()}

      {/* ── 3. Économie ──────────────────────────────────────────── */}
      <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 700 }}>
        Économie
      </Typography>
      <Grid container spacing={2} sx={{ mt: 0.5, mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile label="Revenu total cumulé" value={fmtMoney(s.totalRevenue || 0)} icon={EuroIcon} accent="success"
            sub="Hors annulations / no-show" to="#/payments" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile label="Revenu ce mois" value={fmtMoney(lastMonth(s.revenueByMonth))} icon={EuroIcon} accent="success"
            sub="Mois en cours" to="#/payments" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile label="Prix moyen séance" value={fmtMoney(lastAvgPrice)} icon={EuroIcon} accent="success"
            sub="Dernier mois" to="#/payments" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {affil.loading ? <Skeleton variant="rounded" height={92} /> :
            <StatTile label="Gains affiliation" value={fmtMoney(affilTotal)} icon={ReferralIcon} accent="success"
              sub={`${fmtNum(affilRows.length)} transactions`} to="#/affiliation-transactions" />}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {ura.loading ? <Skeleton variant="rounded" height={92} /> :
            <StatTile label="Ura distribués" value={fmtNum(uraEarned)} icon={ReferralIcon} accent="warning"
              sub={`${fmtNum(uraSpent)} dépensés`} to="#/ura-transactions" />}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {discounts.loading ? <Skeleton variant="rounded" height={92} /> :
            <StatTile label="Réductions" value={fmtNum(discountRows.length)} icon={EuroIcon} accent="warning"
              sub={`${discountUsed} utilisées, ${discountUnused} en attente`} to="#/discount-bonus" />}
        </Grid>
      </Grid>

      {/* ── 4. Communication ──────────────────────────────────────── */}
      <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 700 }}>
        Communication
      </Typography>
      <Grid container spacing={2} sx={{ mt: 0.5, mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          {emails.loading ? <Skeleton variant="rounded" height={92} /> :
            <StatTile label="Emails envoyés" value={fmtNum(emailSent)} icon={EmailIcon} accent="success"
              sub={`Sur ${fmtNum(emailRows.length)} total`} to="#/email-outbox" />}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {emails.loading ? <Skeleton variant="rounded" height={92} /> :
            <StatTile label="Emails en attente" value={fmtNum(emailPending)} icon={PendingIcon} accent="warning"
              to="#/email-outbox" />}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {emails.loading ? <Skeleton variant="rounded" height={92} /> :
            <StatTile label="Emails échoués" value={fmtNum(emailFailed)} icon={FlagIcon}
              accent={emailFailed > 0 ? "error" : "success"} to="#/email-outbox" />}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile label="Conversations IA" value="Voir détails" icon={ArticleIcon} accent="info"
            sub="Sessions in-memory" to="#/chatgpt-sessions" />
        </Grid>
      </Grid>

      {/* ── 5. Top listes ────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          {loadingHero ? <Skeleton variant="rounded" height={320} /> : (
            <Section title="Top spécialités (par RDV)">
              <TopTable data={s.bookingsBySpeciality} label="Spécialité" valueLabel="RDV" />
            </Section>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {loadingHero ? <Skeleton variant="rounded" height={320} /> : (
            <Section title="Top métiers (par RDV)">
              <TopTable data={s.bookingsByJob} label="Métier" valueLabel="RDV" />
            </Section>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {loadingHero ? <Skeleton variant="rounded" height={320} /> : (
            <Section title="Professionnels par métier">
              <TopTable data={s.prosByJob} label="Métier" valueLabel="Pros" />
            </Section>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {loadingHero ? <Skeleton variant="rounded" height={320} /> : (
            <Section title="Pros par tranche d'âge">
              <TopTable data={s.prosByAgeRange} label="Tranche" valueLabel="Pros" />
            </Section>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {loadingHero ? <Skeleton variant="rounded" height={320} /> : (
            <Section title="Prix moyen par mois">
              <TopTable data={s.averagePriceByMonth} label="Mois" valueLabel="€" money topN={12} />
            </Section>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <Section title="État de la file emails">
            {emails.loading ? <LinearProgress /> : (
              <TopTable data={emailByStatus} label="Statut" valueLabel="Emails" />
            )}
          </Section>
        </Grid>
        <Grid item xs={12} md={6}>
          {loadingHero ? <Skeleton variant="rounded" height={320} /> : (
            <Section title="RDV par statut (funnel)">
              <TopTable data={s.bookingsByStatus} label="Statut" valueLabel="RDV" topN={20} />
            </Section>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {loadingHero ? <Skeleton variant="rounded" height={320} /> : (
            <Section title="Revenu par mois">
              <TopTable data={s.revenueByMonth} label="Mois" valueLabel="€" money topN={12} />
            </Section>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {loadingHero ? <Skeleton variant="rounded" height={320} /> : (
            <Section title="Top spécialités (€)">
              <TopTable data={s.revenueBySpeciality} label="Spécialité" valueLabel="€" money />
            </Section>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {loadingHero ? <Skeleton variant="rounded" height={320} /> : (
            <Section title="Top professionnels par revenu">
              <TopTable data={s.revenueByPro} label="Pro" valueLabel="€" money />
            </Section>
          )}
        </Grid>
      </Grid>

      {/* ── 6. Classements ───────────────────────────────────────── */}
      <Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 700 }}>
        Classements
      </Typography>
      <Grid container spacing={2} sx={{ mt: 0.5, mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Section title="Top 10 professionnels (points)" action={
            <Chip size="small" component="a" href="#/ranking" clickable label="Voir tout →" variant="outlined" />}>
            {proRanking.loading ? <LinearProgress /> : (() => {
              const entries = Array.isArray(proRanking.data?.entries) ? proRanking.data.entries : [];
              if (entries.length === 0) return <Typography variant="body2" color="text.secondary">Aucun pro classé</Typography>;
              return (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Pro</TableCell>
                      <TableCell>Métier</TableCell>
                      <TableCell align="right">Points</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entries.map((e) => (
                      <TableRow key={e.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{e.rank}</TableCell>
                        <TableCell>
                          <a href={`#/professionals/${e.id}/show`} style={{ color: "#0d7377", fontWeight: 600 }}>
                            {e.firstName} {e.lastName}
                          </a>
                        </TableCell>
                        <TableCell>{e.job || "—"}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtNum(e.points)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              );
            })()}
          </Section>
        </Grid>
        <Grid item xs={12} md={6}>
          <Section title="Top 10 patients (ArcA)" action={
            <Chip size="small" component="a" href="#/patient-ranking" clickable label="Voir tout →" variant="outlined" />}>
            {patientRanking.loading ? <LinearProgress /> : (() => {
              const entries = Array.isArray(patientRanking.data?.entries) ? patientRanking.data.entries : [];
              if (entries.length === 0) return <Typography variant="body2" color="text.secondary">Aucun patient classé</Typography>;
              return (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Patient</TableCell>
                      <TableCell align="right">ArcA</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entries.map((e) => (
                      <TableRow key={e.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{e.rank}</TableCell>
                        <TableCell>
                          <a href={`#/patients/${e.id}/show`} style={{ color: "#0d7377", fontWeight: 600 }}>
                            {e.firstName} {e.lastName}
                          </a>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtNum(e.arca)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              );
            })()}
          </Section>
        </Grid>
      </Grid>

      {/* ── 7. Footer ressources ─────────────────────────────────── */}
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1, gap: 1 }}>
        <Chip label="Tableau de bord" component="a" href="#/" clickable variant="outlined" />
        <Chip label="Ambassadeurs" component="a" href="#/ambassadors" clickable variant="outlined" />
        <Chip label="Sessions IA" component="a" href="#/chatgpt-sessions" clickable variant="outlined" />
        <Chip label="Suggestions" component="a" href="#/suggestions-specialities" clickable variant="outlined" />
        <Chip label="Signalements" component="a" href="#/reportedUsers" clickable variant="outlined" />
      </Stack>
    </Box>
  );
};

export default { list: StatsScreen };
